import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Spinner, Alert, Table, Nav, Tab, Form } from 'react-bootstrap';
import { FiArrowLeft, FiCloudOff, FiUser, FiBriefcase, FiMapPin, FiCalendar, FiTag, FiGlobe, FiCheckCircle, FiCloudRain } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { getActivityForecastNotTriggeredById, checkMeteoFranceObservationsForForecastNotTriggered, checkGreenRiverMeteoFranceObservationsForForecastNotTriggered, checkMeteostatObservationsForForecastNotTriggered, checkGreenRiverMeteostatObservationsForForecastNotTriggered } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import WeatherDataSections from '../../components/Common/WeatherDataSections';
import PolicySumInsuredRows from '../../components/Common/PolicySumInsuredFields';
import './AdminTripPolicyDetail.css';

const Label = ({ children }) => (
    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</div>
);

const Value = ({ children }) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children || 'N/A'}</div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const getDeep = (obj, path, fallback = 'N/A') => {
    try {
        const val = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
        return val ?? fallback;
    } catch {
        return fallback;
    }
};

const AdminActivityForecastNotTriggeredDetail = () => {
    const navigate = useNavigate();
    const { forecastNotTriggeredId } = useParams();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState('main-info');

    // States for MeteoFrance Observations Check
    const [meteoFranceLoading, setMeteoFranceLoading] = useState(false);
    const [meteoFranceError, setMeteoFranceError] = useState(null);
    const [meteoFranceData, setMeteoFranceData] = useState(null);
    const [showMeteoFranceDetails, setShowMeteoFranceDetails] = useState(false);

    // States for Meteostat Observations Check
    const [meteostatLoading, setMeteostatLoading] = useState(false);
    const [meteostatError, setMeteostatError] = useState(null);
    const [meteostatData, setMeteostatData] = useState(null);
    const [showMeteostatDetails, setShowMeteostatDetails] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            if (!forecastNotTriggeredId) return;

            try {
                setLoading(true);
                setError(null);
                const response = await getActivityForecastNotTriggeredById(forecastNotTriggeredId);
                setData(response || null);
            } catch (e) {
                setError(handleApiError(e, t('pages.activityForecastNotTriggeredDetail.errorLoading')));
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forecastNotTriggeredId]);

    const handleCheckMeteoFranceObservations = async () => {
        if (!forecastNotTriggeredId) {
            setMeteoFranceError(t('pages.activityForecastNotTriggeredDetail.meteofrance.error'));
            return;
        }

        try {
            setMeteoFranceLoading(true);
            setMeteoFranceError(null);
            setMeteoFranceData(null);
            const response = await checkMeteoFranceObservationsForForecastNotTriggered(forecastNotTriggeredId);
            setMeteoFranceData(response);

            // Rafraîchir les données après la vérification
            const updatedData = await getActivityForecastNotTriggeredById(forecastNotTriggeredId);
            setData(updatedData || null);
        } catch (e) {
            setMeteoFranceError(handleApiError(e, t('pages.activityForecastNotTriggeredDetail.meteofrance.error')));
        } finally {
            setMeteoFranceLoading(false);
        }
    };

    const handleCheckGreenRiverMeteoFranceObservations = async () => {
        if (!forecastNotTriggeredId) {
            setMeteoFranceError(t('pages.activityForecastNotTriggeredDetail.meteofrance.greenRiverError'));
            return;
        }

        try {
            setMeteoFranceLoading(true);
            setMeteoFranceError(null);
            setMeteoFranceData(null);
            const response = await checkGreenRiverMeteoFranceObservationsForForecastNotTriggered(forecastNotTriggeredId);
            setMeteoFranceData(response);

            const updatedData = await getActivityForecastNotTriggeredById(forecastNotTriggeredId);
            setData(updatedData || null);
        } catch (e) {
            setMeteoFranceError(handleApiError(e, t('pages.activityForecastNotTriggeredDetail.meteofrance.greenRiverError')));
        } finally {
            setMeteoFranceLoading(false);
        }
    };

    const handleCheckMeteostatObservations = async () => {
        if (!forecastNotTriggeredId) {
            setMeteostatError(t('pages.activityForecastNotTriggeredDetail.meteostat.error'));
            return;
        }

        try {
            setMeteostatLoading(true);
            setMeteostatError(null);
            setMeteostatData(null);
            const response = await checkMeteostatObservationsForForecastNotTriggered(forecastNotTriggeredId);
            setMeteostatData(response);

            // Rafraîchir les données après la vérification
            const updatedData = await getActivityForecastNotTriggeredById(forecastNotTriggeredId);
            setData(updatedData || null);
        } catch (e) {
            setMeteostatError(handleApiError(e, t('pages.activityForecastNotTriggeredDetail.meteostat.error')));
        } finally {
            setMeteostatLoading(false);
        }
    };

    const handleCheckGreenRiverMeteostatObservations = async () => {
        if (!forecastNotTriggeredId) {
            setMeteostatError(t('pages.activityForecastNotTriggeredDetail.meteostat.greenRiverError'));
            return;
        }

        try {
            setMeteostatLoading(true);
            setMeteostatError(null);
            setMeteostatData(null);
            const response = await checkGreenRiverMeteostatObservationsForForecastNotTriggered(forecastNotTriggeredId);
            setMeteostatData(response);

            const updatedData = await getActivityForecastNotTriggeredById(forecastNotTriggeredId);
            setData(updatedData || null);
        } catch (e) {
            setMeteostatError(handleApiError(e, t('pages.activityForecastNotTriggeredDetail.meteostat.greenRiverError')));
        } finally {
            setMeteostatLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="dashboard-page admin-trip-policy-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">{t('pages.activityForecastNotTriggeredDetail.title')}</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/activity/forecasts-not-triggered')}>
                                <FiArrowLeft className="me-2" /> {t('common.back')}
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">{t('pages.activityForecastNotTriggeredDetail.loading')}</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-page admin-trip-policy-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">{t('pages.activityForecastNotTriggeredDetail.title')}</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/activity/forecasts-not-triggered')}>
                                <FiArrowLeft className="me-2" /> {t('common.back')}
                            </Button>
                        </div>
                        <Alert variant="danger">{error}</Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="dashboard-page admin-trip-policy-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">{t('pages.activityForecastNotTriggeredDetail.title')}</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/activity/forecasts-not-triggered')}>
                                <FiArrowLeft className="me-2" /> {t('common.back')}
                            </Button>
                        </div>
                        <p className="text-muted">{t('pages.activityForecastNotTriggeredDetail.notFound')}: {forecastNotTriggeredId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const policyInfo = data?.policy_info || {};
    const clientInfo = data?.client_info || {};
    const activityInfo = data?.activity_info || {};
    const partnerInfo = data?.partner_info || {};
    const referenceInfo = data?.reference_info || {};
    const subCoverage = referenceInfo?.SubCoverage || {};
    const checkClaims = data?.CheckClaims || {};
    const lastObservationInfo = data?.last_observation_info || {};
    const forecastInfo = data?.forecast_info || {};
    const meteomaticsObservationsInfo = data?.meteomatics_observations_info || {};
    const meteofranceObservationsInfo = data?.meteofrance_observations_info || {};
    const meteostatObservationsInfo = data?.meteostat_observations_info || {};
    const isGreenRiverPartner = String(partnerInfo?.company_name || '').toLowerCase().includes('green river');

    return (
        <div className="dashboard-page admin-trip-policy-detail-page">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h2 className="page-title">
                            <FiCloudOff className="me-2" />
                            {t('pages.activityForecastNotTriggeredDetail.title')}
                        </h2>
                        <Button variant="outline-secondary" onClick={() => navigate('/activity/forecasts-not-triggered')}>
                            <FiArrowLeft className="me-2" /> {t('common.back')}
                        </Button>
                    </div>

                    <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'main-info')}>
                        <Nav variant="tabs" className="mb-3">
                            <Nav.Item>
                                <Nav.Link eventKey="main-info">
                                    {t('pages.activityForecastNotTriggeredDetail.tabs.mainInfo')}
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="meteofrance-check">
                                    <FiCloudRain className="me-2" />
                                    {t('pages.activityForecastNotTriggeredDetail.tabs.meteofranceCheck')}
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="meteostat-check">
                                    <FiCloudRain className="me-2" />
                                    {t('pages.activityForecastNotTriggeredDetail.tabs.meteostatCheck')}
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>

                        <Tab.Content>
                            <Tab.Pane eventKey="main-info">
                                {/* Informations principales */}
                    <Section title={t('pages.activityForecastNotTriggeredDetail.sections.mainInfo')}>
                        <Row>
                            <Col md={6}>
                                <Label>ID Prévision non déclenchée</Label>
                                <Value>{data.forecast_not_triggered_id || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Statut</Label>
                                <Value>
                                    <Badge bg={data.status === 'Paid' ? 'success' : data.status === 'Created' ? 'info' : 'secondary'}>
                                        {data.status || 'N/A'}
                                    </Badge>
                                </Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Date de création</Label>
                                <Value>{data.created_date || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Coordonnées</Label>
                                <Value>{data.latitude || 'N/A'}, {data.longitude || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Créé par</Label>
                                <Value>{data.created_by || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Check Claim ID</Label>
                                <Value>{data.check_claim_id || 'N/A'}</Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Informations de la police */}
                    <Section title={t('pages.activityForecastNotTriggeredDetail.sections.policyInfo')}>
                        <Row>
                            <Col md={6}>
                                <Label>Code police</Label>
                                <Value>{policyInfo.policy_code || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Statut</Label>
                                <Value>
                                    <Badge bg={
                                        policyInfo.status === 'Created' ? 'success' :
                                            policyInfo.status === 'Paid' ? 'primary' :
                                                policyInfo.status === 'CG confirmed' ? 'info' :
                                                    policyInfo.status === 'Cancelled' ? 'danger' : 'secondary'
                                    }>
                                        {policyInfo.status || 'N/A'}
                                    </Badge>
                                </Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Date UW</Label>
                                <Value>{policyInfo.uw_date || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Prix assurance (HT TSCA)</Label>
                                <Value>{policyInfo.insurance_price_excl_tsca || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Prix incluant TSCA</Label>
                                <Value>{policyInfo.price_incl_tsca || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Ratio Orakle (HT TSCA)</Label>
                                <Value>{policyInfo.orakle_price_ratio_excl_tsca || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <PolicySumInsuredRows source={policyInfo} Label={Label} Value={Value} />
                    </Section>

                    {/* Informations client */}
                    <Section title={t('pages.activityForecastNotTriggeredDetail.sections.clientInfo')}>
                        <Row>
                            <Col md={6}>
                                <Label>Nom complet</Label>
                                <Value>{clientInfo.first_name || ''} {clientInfo.last_name || ''}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Email</Label>
                                <Value>{clientInfo.email || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Code client</Label>
                                <Value>{clientInfo.client_code || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Statut</Label>
                                <Value>
                                    <Badge bg={clientInfo.status === 'Active' ? 'success' : 'secondary'}>
                                        {clientInfo.status || 'N/A'}
                                    </Badge>
                                </Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Ville</Label>
                                <Value>{clientInfo.city || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Pays</Label>
                                <Value>{clientInfo.country || 'N/A'}</Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Informations activité */}
                    <Section title={t('pages.activityForecastNotTriggeredDetail.sections.activityInfo')}>
                        <Row>
                            <Col md={6}>
                                <Label>Nom</Label>
                                <Value>{activityInfo.name || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Code activité</Label>
                                <Value>{activityInfo.activity_code || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Date début</Label>
                                <Value>{activityInfo.activity_start_date || 'N/A'} {activityInfo.activity_start_time || ''}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Date fin</Label>
                                <Value>{activityInfo.activity_end_date || 'N/A'} {activityInfo.activity_end_time || ''}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Adresse</Label>
                                <Value>{activityInfo.activity_address || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Ville / Pays</Label>
                                <Value>{activityInfo.activity_city || 'N/A'} / {activityInfo.activity_country || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Prix</Label>
                                <Value>{activityInfo.activity_price || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Numéro de réservation</Label>
                                <Value>{activityInfo.reservation_number || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Fournisseur</Label>
                                <Value>{activityInfo.supplier_name || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Adresse fournisseur</Label>
                                <Value>{activityInfo.supplier_address || 'N/A'}</Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Informations partenaire */}
                    <Section title={t('pages.activityForecastNotTriggeredDetail.sections.partnerInfo')}>
                        <Row>
                            <Col md={6}>
                                <Label>Nom complet</Label>
                                <Value>{partnerInfo.partner_first_name || ''} {partnerInfo.partner_last_name || ''}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Email</Label>
                                <Value>{partnerInfo.email || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Nom de l'entreprise</Label>
                                <Value>{partnerInfo.company_name || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Code</Label>
                                <Value>{partnerInfo.code || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Ville / Pays</Label>
                                <Value>{partnerInfo.city || 'N/A'} / {partnerInfo.country || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Commission</Label>
                                <Value>{partnerInfo.commission || 'N/A'}</Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Informations référence */}
                    <Section title={t('pages.activityForecastNotTriggeredDetail.sections.referenceInfo')}>
                        <Row>
                            <Col md={6}>
                                <Label>Nom</Label>
                                <Value>{referenceInfo.reference_name || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Code référence</Label>
                                <Value>{referenceInfo.reference_code || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Catégorie</Label>
                                <Value>{referenceInfo.reference_category || 'N/A'}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Type de garantie</Label>
                            </Col>
                        </Row>
                    </Section>

                    {/* Informations sous-couverture */}
                    {subCoverage && Object.keys(subCoverage).length > 0 && (
                        <Section title={t('pages.activityForecastNotTriggeredDetail.sections.subCoverage')}>
                            <Row>
                                <Col md={6}>
                                    <Label>Type de déclencheur</Label>
                                    <Value>{subCoverage.trigger_type || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Unité</Label>
                                    <Value>{subCoverage.unit || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Déclencheur inférieur</Label>
                                    <Value>{subCoverage.trigger_low || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Déclencheur supérieur</Label>
                                    <Value>{subCoverage.trigger_sup || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Heure d'observation</Label>
                                    <Value>{subCoverage.observation_timing_hour || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Jour d'observation</Label>
                                    <Value>{subCoverage.observation_timing_day || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Ratio SIV maintenu</Label>
                                    <Value>{subCoverage.siv_ratio_maintained || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Ratio SIV annulé</Label>
                                    <Value>{subCoverage.siv_ratio_cancelled || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Ville</Label>
                                    <Value>{subCoverage.city || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Code postal</Label>
                                    <Value>{subCoverage.zip_code || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Source de données</Label>
                                    <Value>{subCoverage.data_source || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Code de couverture</Label>
                                    <Value>{subCoverage.coverage_code || 'N/A'}</Value>
                                </Col>
                            </Row>
                        </Section>
                    )}

                    {/* Check Claims */}
                    {checkClaims && Object.keys(checkClaims).length > 0 && (
                        <Section title={t('pages.activityForecastNotTriggeredDetail.sections.checkClaims')}>
                            <Row>
                                <Col md={6}>
                                    <Label>Déclenché observation</Label>
                                    <Value>
                                        {(() => {
                                            const isTriggeredObs = checkClaims.is_triggered_observation;

                                            if (isTriggeredObs === null) {
                                                return (
                                                    <Badge bg="warning">
                                                        En attente de traitement
                                                    </Badge>
                                                );
                                            }

                                            return (
                                                <Badge bg={isTriggeredObs === true ? 'success' : 'danger'}>
                                                    {isTriggeredObs === true ? 'Oui' : 'Non'}
                                                </Badge>
                                            );
                                        })()}
                                    </Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Heures déclenchées</Label>
                                    <Value>{checkClaims.count_hours_triggered || 0}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Déclenché prévision</Label>
                                    <Value>
                                        <Badge bg={checkClaims.is_triggered_forecast ? 'success' : 'secondary'}>
                                            {checkClaims.is_triggered_forecast ? 'Oui' : 'Non'}
                                        </Badge>
                                    </Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Déclenché cumulé</Label>
                                    <Value>
                                        <Badge bg={checkClaims.prcp_is_cumulated ? 'success' : 'secondary'}>
                                            {checkClaims.prcp_is_cumulated ? 'Oui' : 'Non'}
                                        </Badge>
                                    </Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Déclencheur inférieur</Label>
                                    <Value>{checkClaims.trigger_low || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Déclencheur supérieur</Label>
                                    <Value>{checkClaims.check_trigger_sup || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Observation vérifiée</Label>
                                    <Value>
                                        <Badge bg={checkClaims.observation_checked ? 'success' : 'secondary'}>
                                            {checkClaims.observation_checked ? 'Oui' : 'Non'}
                                        </Badge>
                                    </Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Prévision vérifiée</Label>
                                    <Value>
                                        <Badge bg={checkClaims.forecast_checked ? 'success' : 'secondary'}>
                                            {checkClaims.forecast_checked ? 'Oui' : 'Non'}
                                        </Badge>
                                    </Value>
                                </Col>
                            </Row>
                        </Section>
                    )}

                    {/* Dernière observation */}
                    {lastObservationInfo && lastObservationInfo.last_observation_id && (
                        <Section title={t('pages.activityForecastNotTriggeredDetail.sections.lastObservation')}>
                            <Row>
                                <Col md={6}>
                                    <Label>Source de données</Label>
                                    <Value>{lastObservationInfo.last_data_source || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Station</Label>
                                    <Value>{lastObservationInfo.last_station_name || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>ID Station</Label>
                                    <Value>{lastObservationInfo.last_station_id || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Coordonnées station</Label>
                                    <Value>{lastObservationInfo.last_latitude_station || 'N/A'}, {lastObservationInfo.last_longitude_station || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Total mm précipitation</Label>
                                    <Value>{lastObservationInfo.last_total_mm_prc || '0,0'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Date de modification</Label>
                                    <Value>{lastObservationInfo.last_observation_last_modified_at || 'N/A'}</Value>
                                </Col>
                            </Row>
                            {lastObservationInfo.last_observation_data && Array.isArray(lastObservationInfo.last_observation_data) && lastObservationInfo.last_observation_data.length > 0 && (
                                <div className="mt-3">
                                    <Label>Données d'observation</Label>
                                    <Table striped bordered hover size="sm" className="mt-2">
                                        <thead>
                                            <tr>
                                                <th>Heure</th>
                                                <th>Précipitation (mm)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {lastObservationInfo.last_observation_data.map((obs, idx) => (
                                                <tr key={idx}>
                                                    <td>{obs.time || 'N/A'}</td>
                                                    <td>{obs.prcp || '0,0'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Section>
                    )}

                    {/* Observations Météofrance */}
                    {meteofranceObservationsInfo && meteofranceObservationsInfo.meteofrance_observation_id && (
                        <Section title={t('pages.activityForecastNotTriggeredDetail.sections.meteofranceObservations')}>
                            <Row>
                                <Col md={6}>
                                    <Label>Source de données</Label>
                                    <Value>{meteofranceObservationsInfo.meteofrance_data_source || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Station</Label>
                                    <Value>{meteofranceObservationsInfo.meteofrance_station_name || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>ID Station</Label>
                                    <Value>{meteofranceObservationsInfo.meteofrance_station_id || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Total mm précipitation</Label>
                                    <Value>{meteofranceObservationsInfo.meteofrance_total_mm_prc || '0,0'}</Value>
                                </Col>
                            </Row>
                            {meteofranceObservationsInfo.meteofrance_observation_data && Array.isArray(meteofranceObservationsInfo.meteofrance_observation_data) && meteofranceObservationsInfo.meteofrance_observation_data.length > 0 && (
                                <div className="mt-3">
                                    <Label>Données d'observation</Label>
                                    <Table striped bordered hover size="sm" className="mt-2">
                                        <thead>
                                            <tr>
                                                <th>Heure</th>
                                                <th>Précipitation (mm)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {meteofranceObservationsInfo.meteofrance_observation_data.map((obs, idx) => (
                                                <tr key={idx}>
                                                    <td>{obs.time || 'N/A'}</td>
                                                    <td>{obs.prcp || '0,0'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Section>
                    )}

                    {/* Observations Meteomatics */}
                    {meteomaticsObservationsInfo && meteomaticsObservationsInfo.meteomatics_observation_id && (
                        <Section title={t('pages.activityForecastNotTriggeredDetail.sections.meteomaticsObservations')}>
                            <Row>
                                <Col md={6}>
                                    <Label>Source de données</Label>
                                    <Value>{meteomaticsObservationsInfo.meteomatics_data_source || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Station</Label>
                                    <Value>{meteomaticsObservationsInfo.meteomatics_station_name || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Total mm précipitation</Label>
                                    <Value>{meteomaticsObservationsInfo.meteomatics_total_mm_prc || '0,0'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Date de modification</Label>
                                    <Value>{meteomaticsObservationsInfo.meteomatics_observation_last_modified_at || 'N/A'}</Value>
                                </Col>
                            </Row>
                        </Section>
                    )}

                    {/* Observations Meteostat */}
                    {meteostatObservationsInfo && meteostatObservationsInfo.meteostat_observation_id && (
                        <Section title={t('pages.activityForecastNotTriggeredDetail.sections.meteostatObservations')}>
                            <Row>
                                <Col md={6}>
                                    <Label>Source de données</Label>
                                    <Value>{meteostatObservationsInfo.meteostat_data_source || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Station</Label>
                                    <Value>{meteostatObservationsInfo.meteostat_station_name || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Total mm précipitation</Label>
                                    <Value>{meteostatObservationsInfo.meteostat_total_mm_prc || '0,0'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Date de modification</Label>
                                    <Value>{meteostatObservationsInfo.meteostat_observation_last_modified_at || 'N/A'}</Value>
                                </Col>
                            </Row>
                        </Section>
                    )}

                    {/* Informations prévision */}
                    {forecastInfo && forecastInfo.forecast_id && (
                        <Section title={t('pages.activityForecastNotTriggeredDetail.sections.forecastInfo')}>
                            <Row>
                                <Col md={6}>
                                    <Label>ID Prévision</Label>
                                    <Value>{forecastInfo.forecast_id || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Source de données</Label>
                                    <Value>{forecastInfo.data_source || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Date de modification</Label>
                                    <Value>{forecastInfo.forecast_last_modified_at || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Total mm précipitation</Label>
                                    <Value>{forecastInfo.total_mm_prc || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <WeatherDataSections
                                weatherData={forecastInfo.weather_data}
                                weatherTemp={forecastInfo.weather_temp}
                                weatherWind={forecastInfo.weather_wind}
                                enableCopy
                                tableSize="sm"
                                bordered
                                className="mt-3"
                                idPrefix="activity-forecast-nt"
                            />
                        </Section>
                    )}
                            </Tab.Pane>

                            <Tab.Pane eventKey="meteofrance-check">
                                <Card>
                                    <Card.Body>
                                <h5 className="mb-3">
                                    <FiCloudRain className="me-2" />
                                    {t('pages.activityForecastNotTriggeredDetail.meteofrance.title')}
                                </h5>
                                <p className="text-muted mb-3">
                                    {t('pages.activityForecastNotTriggeredDetail.meteofrance.description')}
                                </p>
                                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                    <div className="d-flex flex-wrap gap-2">
                                        <Button
                                            variant="primary"
                                            onClick={handleCheckMeteoFranceObservations}
                                            disabled={meteoFranceLoading || !forecastNotTriggeredId}
                                        >
                                            {meteoFranceLoading ? (
                                                <>
                                                    <Spinner size="sm" animation="border" className="me-2" />
                                                    {t('pages.activityForecastNotTriggeredDetail.meteofrance.checking')}
                                                </>
                                            ) : (
                                                <>
                                                    <FiCloudRain className="me-2" />
                                                    {t('pages.activityForecastNotTriggeredDetail.meteofrance.checkObservations')}
                                                </>
                                            )}
                                        </Button>
                                        {isGreenRiverPartner && (
                                            <Button
                                                variant="success"
                                                onClick={handleCheckGreenRiverMeteoFranceObservations}
                                                disabled={meteoFranceLoading || !forecastNotTriggeredId}
                                            >
                                                {meteoFranceLoading ? (
                                                    <>
                                                        <Spinner size="sm" animation="border" className="me-2" />
                                                        {t('pages.activityForecastNotTriggeredDetail.meteofrance.checking')}
                                                    </>
                                                ) : (
                                                    <>
                                                        <FiCloudRain className="me-2" />
                                                        {t('pages.activityForecastNotTriggeredDetail.meteofrance.checkObservationsGreenRiver')}
                                                    </>
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                            {meteoFranceData && (
                                                <Form.Check
                                                    type="switch"
                                                    id="toggle-meteofrance-details"
                                                    label="Afficher les détails"
                                                    checked={showMeteoFranceDetails}
                                                    onChange={(e) => setShowMeteoFranceDetails(e.target.checked)}
                                                />
                                            )}
                                        </div>
                                        {meteoFranceError && (
                                            <Alert variant="danger" className="mt-3">
                                                <strong>Erreur :</strong> {meteoFranceError}
                                            </Alert>
                                        )}
                                        {meteoFranceData && (
                                            <div className="mt-3">
                                                <Alert variant="success" className="mb-3">
                                                    <strong>Vérification terminée avec succès !</strong>
                                                </Alert>
                                                {showMeteoFranceDetails ? (
                                                    <Card>
                                                        <Card.Header>
                                                            <strong>Résultats de la vérification</strong>
                                                        </Card.Header>
                                                        <Card.Body>
                                                            {meteoFranceData.forecast_not_triggered_id && (
                                                                <p className="mb-2">
                                                                    <strong>ID de la prévision non déclenchée :</strong> <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{meteoFranceData.forecast_not_triggered_id}</span>
                                                                </p>
                                                            )}
                                                            {meteoFranceData.observation_id && (
                                                                <p className="mb-2">
                                                                    <strong>ID de l'observation :</strong> <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{meteoFranceData.observation_id}</span>
                                                                </p>
                                                            )}
                                                            {meteoFranceData.is_triggered_observation !== undefined && (
                                                                <p className="mb-2">
                                                                    <strong>Observation déclenchée :</strong>
                                                                    <Badge bg={meteoFranceData.is_triggered_observation ? 'success' : 'warning'} className="ms-2">
                                                                        {meteoFranceData.is_triggered_observation ? 'Oui' : 'Non'}
                                                                    </Badge>
                                                                </p>
                                                            )}
                                                            {meteoFranceData.data_source && (
                                                                <p className="mb-2">
                                                                    <strong>Source des données :</strong> {meteoFranceData.data_source}
                                                                </p>
                                                            )}
                                                            {meteoFranceData.total_mm_prc !== undefined && (
                                                                <p className="mb-2">
                                                                    <strong>Total précipitations :</strong> {meteoFranceData.total_mm_prc} mm
                                                                </p>
                                                            )}
                                                            {meteoFranceData.station_name && (
                                                                <p className="mb-2">
                                                                    <strong>Station météorologique :</strong> {meteoFranceData.station_name}
                                                                </p>
                                                            )}
                                                            {meteoFranceData.status && (
                                                                <p className="mb-2">
                                                                    <strong>Statut :</strong> {meteoFranceData.status}
                                                                </p>
                                                            )}
                                                            {meteoFranceData.message && (
                                                                <Alert variant="info" className="mt-3">
                                                                    {meteoFranceData.message}
                                                                </Alert>
                                                            )}
                                                            <div className="mt-3">
                                                                <Form.Label><strong>Données complètes :</strong></Form.Label>
                                                                <pre style={{
                                                                    whiteSpace: 'pre-wrap',
                                                                    wordBreak: 'break-word',
                                                                    fontSize: '0.85rem',
                                                                    maxHeight: '300px',
                                                                    overflowY: 'auto',
                                                                    backgroundColor: '#f8f9fa',
                                                                    padding: '1rem',
                                                                    borderRadius: '0.25rem'
                                                                }}>
                                                                    {JSON.stringify(meteoFranceData, null, 2)}
                                                                </pre>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                ) : (
                                                    <Card>
                                                        <Card.Body>
                                                            <Row>
                                                                {meteoFranceData.is_triggered_observation !== undefined && (
                                                                    <Col md={6}>
                                                                        <p className="mb-2">
                                                                            <strong>Observation déclenchée :</strong>
                                                                            <Badge bg={meteoFranceData.is_triggered_observation ? 'success' : 'warning'} className="ms-2">
                                                                                {meteoFranceData.is_triggered_observation ? 'Oui' : 'Non'}
                                                                            </Badge>
                                                                        </p>
                                                                    </Col>
                                                                )}
                                                                {meteoFranceData.data_source && (
                                                                    <Col md={6}>
                                                                        <p className="mb-2">
                                                                            <strong>Source :</strong> {meteoFranceData.data_source}
                                                                        </p>
                                                                    </Col>
                                                                )}
                                                                {meteoFranceData.total_mm_prc !== undefined && (
                                                                    <Col md={6}>
                                                                        <p className="mb-2">
                                                                            <strong>Précipitations :</strong> {meteoFranceData.total_mm_prc} mm
                                                                        </p>
                                                                    </Col>
                                                                )}
                                                                {meteoFranceData.status && (
                                                                    <Col md={6}>
                                                                        <p className="mb-2">
                                                                            <strong>Statut :</strong> {meteoFranceData.status}
                                                                        </p>
                                                                    </Col>
                                                                )}
                                                            </Row>
                                                            {meteoFranceData.message && (
                                                                <Alert variant="info" className="mt-3">
                                                                    {meteoFranceData.message}
                                                                </Alert>
                                                            )}
                                                        </Card.Body>
                                                    </Card>
                                                )}
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Tab.Pane>

                            <Tab.Pane eventKey="meteostat-check">
                                <Card>
                                    <Card.Body>
                                        <h5 className="mb-3">
                                            <FiCloudRain className="me-2" />
                                            Créer ou mettre à jour les observations Meteostat
                                        </h5>
                                        <p className="text-muted mb-3">
                                            Cette fonction crée ou met à jour les observations météorologiques Meteostat pour cette prévision non déclenchée.
                                            Elle récupère les données depuis l'API Meteostat, trouve la station la plus proche et vérifie si les seuils de déclenchement sont atteints.
                                        </p>
                                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                            <div className="d-flex flex-wrap gap-2">
                                                <Button
                                                    variant="primary"
                                                    onClick={handleCheckMeteostatObservations}
                                                    disabled={meteostatLoading || !forecastNotTriggeredId}
                                                >
                                                    {meteostatLoading ? (
                                                        <>
                                                            <Spinner size="sm" animation="border" className="me-2" />
                                                            {t('pages.activityForecastNotTriggeredDetail.meteostat.processing')}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FiCloudRain className="me-2" />
                                                            {t('pages.activityForecastNotTriggeredDetail.meteostat.createUpdate')}
                                                        </>
                                                    )}
                                                </Button>
                                                {isGreenRiverPartner && (
                                                    <Button
                                                        variant="success"
                                                        onClick={handleCheckGreenRiverMeteostatObservations}
                                                        disabled={meteostatLoading || !forecastNotTriggeredId}
                                                    >
                                                        {meteostatLoading ? (
                                                            <>
                                                                <Spinner size="sm" animation="border" className="me-2" />
                                                                {t('pages.activityForecastNotTriggeredDetail.meteostat.processing')}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FiCloudRain className="me-2" />
                                                                {t('pages.activityForecastNotTriggeredDetail.meteostat.createUpdateGreenRiver')}
                                                            </>
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                            {meteostatData && (
                                                <Form.Check
                                                    type="switch"
                                                    id="toggle-meteostat-details"
                                                    label="Afficher les détails"
                                                    checked={showMeteostatDetails}
                                                    onChange={(e) => setShowMeteostatDetails(e.target.checked)}
                                                />
                                            )}
                                        </div>
                                        {meteostatError && (
                                            <Alert variant="danger" className="mt-3">
                                                <strong>Erreur :</strong> {meteostatError}
                                            </Alert>
                                        )}
                                        {meteostatData && (
                                            <div className="mt-3">
                                                <Alert variant="success" className="mb-3">
                                                    <strong>Observation créée/mise à jour avec succès !</strong>
                                                </Alert>
                                                {showMeteostatDetails ? (
                                                    <Card>
                                                        <Card.Header>
                                                            <strong>Résultats détaillés</strong>
                                                        </Card.Header>
                                                        <Card.Body>
                                                            {meteostatData.message && (
                                                                <Alert variant="info" className="mb-3">
                                                                    {meteostatData.message}
                                                                </Alert>
                                                            )}
                                                            {meteostatData.check_claims && (
                                                                <div className="mb-3">
                                                                    <h6>Informations du check claim</h6>
                                                                    {meteostatData.check_claims.check_claim_id && (
                                                                        <p className="mb-2">
                                                                            <strong>ID du check claim :</strong> <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{meteostatData.check_claims.check_claim_id}</span>
                                                                        </p>
                                                                    )}
                                                                    {meteostatData.check_claims.is_triggered_observation !== undefined && (
                                                                        <p className="mb-2">
                                                                            <strong>Observation déclenchée :</strong>
                                                                            <Badge bg={meteostatData.check_claims.is_triggered_observation ? 'success' : 'warning'} className="ms-2">
                                                                                {meteostatData.check_claims.is_triggered_observation ? 'Oui' : 'Non'}
                                                                            </Badge>
                                                                        </p>
                                                                    )}
                                                                    {meteostatData.check_claims.observation_checked !== undefined && (
                                                                        <p className="mb-2">
                                                                            <strong>Observation vérifiée :</strong> {meteostatData.check_claims.observation_checked ? 'Oui' : 'Non'}
                                                                        </p>
                                                                    )}
                                                                    {meteostatData.check_claims.count_hours_triggered !== undefined && (
                                                                        <p className="mb-2">
                                                                            <strong>Nombre d'heures déclenchées :</strong> {meteostatData.check_claims.count_hours_triggered}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {meteostatData.observation_info && (
                                                                <div className="mb-3">
                                                                    <h6>Informations de l'observation</h6>
                                                                    {meteostatData.observation_info.observation_id && (
                                                                        <p className="mb-2">
                                                                            <strong>ID de l'observation :</strong> <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{meteostatData.observation_info.observation_id}</span>
                                                                        </p>
                                                                    )}
                                                                    {meteostatData.observation_info.data_source && (
                                                                        <p className="mb-2">
                                                                            <strong>Source des données :</strong> {meteostatData.observation_info.data_source}
                                                                        </p>
                                                                    )}
                                                                    {meteostatData.observation_info.total_mm_prc !== undefined && (
                                                                        <p className="mb-2">
                                                                            <strong>Total précipitations :</strong> {meteostatData.observation_info.total_mm_prc} mm
                                                                        </p>
                                                                    )}
                                                                    {meteostatData.observation_info.station_name && (
                                                                        <p className="mb-2">
                                                                            <strong>Station météorologique :</strong> {meteostatData.observation_info.station_name}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {meteostatData.forecast_not_triggered_info && (
                                                                <div className="mb-3">
                                                                    <h6>Informations de la prévision non déclenchée</h6>
                                                                    {meteostatData.forecast_not_triggered_info.forecast_not_triggered_id && (
                                                                        <p className="mb-2">
                                                                            <strong>ID de la prévision non déclenchée :</strong> <span style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{meteostatData.forecast_not_triggered_info.forecast_not_triggered_id}</span>
                                                                        </p>
                                                                    )}
                                                                    {meteostatData.forecast_not_triggered_info.status && (
                                                                        <p className="mb-2">
                                                                            <strong>Statut :</strong> {meteostatData.forecast_not_triggered_info.status}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )}
                                                            <div className="mt-3">
                                                                <Form.Label><strong>Données complètes :</strong></Form.Label>
                                                                <pre style={{
                                                                    whiteSpace: 'pre-wrap',
                                                                    wordBreak: 'break-word',
                                                                    fontSize: '0.85rem',
                                                                    maxHeight: '300px',
                                                                    overflowY: 'auto',
                                                                    backgroundColor: '#f8f9fa',
                                                                    padding: '1rem',
                                                                    borderRadius: '0.25rem'
                                                                }}>
                                                                    {JSON.stringify(meteostatData, null, 2)}
                                                                </pre>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                ) : (
                                                    <Card>
                                                        <Card.Body>
                                                            <Row>
                                                                {meteostatData.check_claims?.is_triggered_observation !== undefined && (
                                                                    <Col md={6}>
                                                                        <p className="mb-2">
                                                                            <strong>Observation déclenchée :</strong>
                                                                            <Badge bg={meteostatData.check_claims.is_triggered_observation ? 'success' : 'warning'} className="ms-2">
                                                                                {meteostatData.check_claims.is_triggered_observation ? 'Oui' : 'Non'}
                                                                            </Badge>
                                                                        </p>
                                                                    </Col>
                                                                )}
                                                                {meteostatData.observation_info?.data_source && (
                                                                    <Col md={6}>
                                                                        <p className="mb-2">
                                                                            <strong>Source :</strong> {meteostatData.observation_info.data_source}
                                                                        </p>
                                                                    </Col>
                                                                )}
                                                                {meteostatData.observation_info?.total_mm_prc !== undefined && (
                                                                    <Col md={6}>
                                                                        <p className="mb-2">
                                                                            <strong>Précipitations :</strong> {meteostatData.observation_info.total_mm_prc} mm
                                                                        </p>
                                                                    </Col>
                                                                )}
                                                                {meteostatData.forecast_not_triggered_info?.status && (
                                                                    <Col md={6}>
                                                                        <p className="mb-2">
                                                                            <strong>Statut :</strong> {meteostatData.forecast_not_triggered_info.status}
                                                                        </p>
                                                                    </Col>
                                                                )}
                                                            </Row>
                                                            {meteostatData.message && (
                                                                <Alert variant="info" className="mt-3">
                                                                    {meteostatData.message}
                                                                </Alert>
                                                            )}
                                                        </Card.Body>
                                                    </Card>
                                                )}
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminActivityForecastNotTriggeredDetail;
